import {useEffect, useMemo, useState} from 'react';
import {useCategoriesWithTags} from '../../common/hooks/useCategories';
import {SearchInput} from '../search/SearchInput';
import {
  mapGetCategoriesResponseToScreenInterests,
  mapTagCodesToScreenInterests,
} from '../../utils/mappers';
import {CategoryWithTags} from './CategoryWithTags';
import {DashButton} from '../button/DashButton';
import {ScreenInterest} from '../screens/TagSelectionScreen';

interface TagSelectorModalProps {
  selectedTagCodes: number[];
  onApply: (tagCodes: number[]) => void;
  onClose: () => void;
}

export const TagSelectorModal = ({
  selectedTagCodes,
  onApply,
  onClose,
}: TagSelectorModalProps) => {
  const [filterSearchText, setFilterSearchText] = useState('');
  const {data: categoriesResponse, isLoading} = useCategoriesWithTags();

  const [selectedInterests, setSelectedInterests] = useState<ScreenInterest[]>([]);

  useEffect(() => {
    if (selectedTagCodes && categoriesResponse) {
      const screenInterests = mapTagCodesToScreenInterests(
        selectedTagCodes,
        categoriesResponse || [],
      );
      setSelectedInterests(screenInterests);
    }
  }, [selectedTagCodes, categoriesResponse]);

  const allInterests = useMemo(() => {
    if (!categoriesResponse) return [];
    return mapGetCategoriesResponseToScreenInterests(categoriesResponse);
  }, [categoriesResponse]);

  const filteredInterests = useMemo(() => {
    const searchText = filterSearchText.toLowerCase();
    if (searchText === '') return allInterests;
    return allInterests.filter(
      interest =>
        interest.tagName.toLowerCase().includes(searchText) ||
        interest.categoryName.toLowerCase().includes(searchText),
    );
  }, [filterSearchText, allInterests]);

  const handleToggleInterest = (interest: ScreenInterest) => {
    setSelectedInterests(prev =>
      prev.some(item => item.tagName === interest.tagName)
        ? prev.filter(item => item.tagName !== interest.tagName)
        : [...prev, interest],
    );
  };

  const handleApply = () => {
    onApply(selectedInterests.map(item => item.tagCode));
    onClose();
  };

  const groupedInterestsByCategory = filteredInterests.reduce(
    (acc, interest) => {
      if (!acc[interest.categoryName]) {
        acc[interest.categoryName] = [];
      }
      acc[interest.categoryName]?.push(interest);
      return acc;
    },
    {} as Record<string, ScreenInterest[] | undefined>,
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.75)',
        zIndex: 2000,
      }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '40px 20px 120px 20px',
        }}
        onClick={e => {
          if (e.target === e.currentTarget) onClose();
        }}>
      <div
        style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '700px',
          padding: '30px 30px 20px 30px',
          position: 'relative',
        }}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            color: '#aaa',
            fontSize: '1.4rem',
            cursor: 'pointer',
            lineHeight: 1,
          }}>
          ✕
        </button>

        <h2 style={{color: '#fff', marginBottom: '24px', textAlign: 'center'}}>
          Select Tags
        </h2>

        <div style={{marginBottom: 30}}>
          <SearchInput onSearch={text => setFilterSearchText(text)} debounceMs={0} />
        </div>

        {isLoading && (
          <p style={{color: '#aaa', textAlign: 'center'}}>Loading tags...</p>
        )}

        <div>
          {filteredInterests.length > 0 &&
            categoriesResponse?.map(({name}) => (
              <CategoryWithTags
                categoryInterests={groupedInterestsByCategory[name] || []}
                categoryName={name}
                selectedInterests={selectedInterests}
                toggleInterest={handleToggleInterest}
                key={name}
              />
            ))}
        </div>

        <div style={{marginTop: 30}}>
          <DashButton text="Apply Selected Tags" onClick={handleApply} />
        </div>
      </div>
      </div>
    </div>
  );
};
